import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

const MOBILE_QUERY = '(max-width: 760px)';

function isMobileViewport() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(MOBILE_QUERY).matches
  );
}

// On phones, long descriptions show only their first few lines with a
// "read more" toggle; on wider screens the text is always shown in full.
// The excerpt height is derived from the text's real line height, so it
// follows font-size changes; the fade and toggle styling live in CSS.
// With `paragraphs`, the excerpt is instead the first N <p> children, cut
// cleanly at a paragraph boundary.
function ClampText({ language, lines = 4, paragraphs = 0, className = '', children }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [fullHeight, setFullHeight] = useState(null);
  const [collapsedHeight, setCollapsedHeight] = useState(null);
  const bodyRef = useRef(null);
  const toggleRef = useRef(null);
  const bodyId = useId();

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) {
      return;
    }
    setFullHeight(el.scrollHeight);
    if (!isMobileViewport()) {
      setOverflowing(false);
      return;
    }
    if (paragraphs > 0) {
      const blocks = el.querySelectorAll(':scope > p');
      if (blocks.length <= paragraphs) {
        setOverflowing(false);
        return;
      }
      const lastShown = blocks[paragraphs - 1].getBoundingClientRect();
      setCollapsedHeight(Math.ceil(lastShown.bottom - el.getBoundingClientRect().top));
      setOverflowing(true);
      return;
    }
    // The excerpt is `lines` lines of the first paragraph's own line height.
    const firstLine = el.querySelector('p') || el;
    const lineHeight = parseFloat(window.getComputedStyle(firstLine).lineHeight) || 0;
    if (lineHeight <= 0) {
      setOverflowing(false);
      return;
    }
    const limit = Math.round(lineHeight * lines);
    setCollapsedHeight(limit);
    setOverflowing(el.scrollHeight > limit + lineHeight / 2);
  }, [lines, paragraphs]);

  useEffect(() => {
    measure();
    const el = bodyRef.current;
    if (typeof window.ResizeObserver === 'function' && el) {
      const observer = new window.ResizeObserver(measure);
      observer.observe(el);
      // The body's own box is pinned by max-height while collapsed, so also
      // watch its children (late web fonts or a language switch reflow them).
      Array.from(el.children).forEach((child) => observer.observe(child));
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, children, language]);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      // Collapsing a long story can leave the reader far below the card;
      // bring the toggle (now just under the excerpt) back into view.
      if (!next) {
        window.requestAnimationFrame(() => {
          toggleRef.current?.scrollIntoView({ block: 'nearest' });
        });
      }
      return next;
    });
  };

  const collapsed = overflowing && !expanded;
  const label = expanded
    ? language === 'ja'
      ? '閉じる'
      : 'Show less'
    : language === 'ja'
      ? '続きを見る'
      : 'Read more';

  return (
    <div className={`clamp-text ${className}`}>
      <div
        id={bodyId}
        ref={bodyRef}
        className={`clamp-text__body ${collapsed ? 'is-collapsed' : ''}`}
        style={
          overflowing
            ? { maxHeight: `${expanded ? fullHeight : collapsedHeight}px` }
            : undefined
        }
      >
        {children}
      </div>
      {overflowing ? (
        <button
          type="button"
          ref={toggleRef}
          className="clamp-text__toggle"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={toggle}
        >
          {label}
          <span className="clamp-text__chevron" aria-hidden="true">
            {expanded ? '▴' : '▾'}
          </span>
        </button>
      ) : null}
    </div>
  );
}

export default ClampText;
